import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { WithWrite } from '../../lib/rbac';
import { settingsApi, type FortnoxSettings } from '../../services/settings';

const DEFAULT_SCOPES = 'companyinformation customer article invoice payment supplier supplierinvoice bookkeeping order price';

type FormState = {
  clientId: string;
  clientSecret: string;
  scopes: string;
};

export function FortnoxSettingsPage() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncingArticles, setSyncingArticles] = useState(false);
  const [status, setStatus] = useState<FortnoxSettings | null>(null);
  const [form, setForm] = useState<FormState>({
    clientId: '',
    clientSecret: '',
    scopes: DEFAULT_SCOPES,
  });

  const hasCallbackMessage = useMemo(
    () => searchParams.has('fortnox') || searchParams.has('fortnox_error'),
    [searchParams]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await settingsApi.getFortnoxSettings();
      setStatus(data);
      setForm({
        clientId: data.clientId ?? '',
        clientSecret: '',
        scopes: data.scopes ?? DEFAULT_SCOPES,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte hämta Fortnox-inställningar');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const connected = searchParams.get('fortnox');
    const error = searchParams.get('fortnox_error');
    if (connected === 'connected') {
      toast.success('Fortnox är nu anslutet');
    } else if (error) {
      toast.error(error);
    } else {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.delete('fortnox');
    next.delete('fortnox_error');
    setSearchParams(next, { replace: true });
    load();
  }, [load, searchParams, setSearchParams, toast]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateFortnoxSettings({
        clientId: form.clientId.trim(),
        clientSecret: form.clientSecret.trim() || undefined,
        scopes: form.scopes.trim() || DEFAULT_SCOPES,
      });
      toast.success('Fortnox-inställningar sparade');
      await load();
      setForm((current) => ({ ...current, clientSecret: '' }));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara Fortnox-inställningar');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const result = await settingsApi.startFortnoxConnect();
      window.location.href = result.authUrl;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte starta Fortnox-inloggning');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await settingsApi.disconnectFortnox();
      toast.success('Fortnox frånkopplat');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte koppla från Fortnox');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncArticles = async () => {
    setSyncingArticles(true);
    try {
      const result = await settingsApi.syncFortnoxArticles();
      toast.success(`Fortnox-artiklar synkade: ${result.created} skapade, ${result.updated} uppdaterade`);
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte synka artiklar till Fortnox');
    } finally {
      setSyncingArticles(false);
    }
  };

  if (loading && !status) {
    return <p className="text-sm text-gray-500">Laddar...</p>;
  }

  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold">Fortnox</h2>
      <p className="text-sm text-gray-600">
        Logga in Fortnox en gång med OAuth. Därefter kan access-token förnyas löpande via refresh-token så länge Fortnox-anslutningen hålls aktiv.
      </p>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,560px)_minmax(0,1fr)]">
        <section className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
          <label className="block text-sm font-medium">Client ID</label>
          <input className="w-full" value={form.clientId} onChange={(event) => setForm((current) => ({ ...current, clientId: event.target.value }))} />
          <label className="block text-sm font-medium">Client Secret</label>
          <input
            type="password"
            className="w-full"
            value={form.clientSecret}
            onChange={(event) => setForm((current) => ({ ...current, clientSecret: event.target.value }))}
            placeholder={status?.hasClientSecret ? 'Lämna tomt för att behålla sparat secret' : ''}
          />
          <label className="block text-sm font-medium">Redirect URI</label>
          <input className="w-full bg-gray-100" value={status?.redirectUri ?? ''} readOnly />
          <label className="block text-sm font-medium">Scopes</label>
          <input className="w-full" value={form.scopes} onChange={(event) => setForm((current) => ({ ...current, scopes: event.target.value }))} />
          <div className="text-xs text-gray-500">Rekommenderat för faktura och nyckeltal: `companyinformation customer article invoice payment supplier supplierinvoice bookkeeping order price`</div>
          <WithWrite moduleCode="settings">
            <div className="flex flex-wrap gap-2 pt-1">
              <button type="button" className="btn btn-success px-2 py-1" onClick={handleSave} disabled={saving}>
                {saving ? 'Sparar...' : 'Spara'}
              </button>
              <button type="button" className="rounded border border-orange-500 px-2 py-1 text-sm hover:bg-orange-50" onClick={handleConnect} disabled={connecting}>
                {connecting ? 'Startar...' : 'Logga in på Fortnox'}
              </button>
              <button type="button" className="rounded border border-red-300 px-2 py-1 text-sm text-red-700 hover:bg-red-50" onClick={handleDisconnect} disabled={disconnecting || !status?.isConnected}>
                {disconnecting ? 'Kopplar från...' : 'Koppla från'}
              </button>
            </div>
          </WithWrite>
        </section>

        <section className="rounded border border-gray-200 bg-white p-3 space-y-3">
          <div className="space-y-1">
            <div className="text-sm"><span className="text-gray-500">Status:</span> <span className={status?.isConnected ? 'font-medium text-green-700' : ''}>{status?.isConnected ? 'Ansluten' : 'Inte ansluten'}</span></div>
            <div className="text-sm"><span className="text-gray-500">Ansluten sedan:</span> {status?.connectedAt ?? '-'}</div>
            <div className="text-sm"><span className="text-gray-500">Token giltig till:</span> {status?.accessTokenExpiresAt ?? '-'}</div>
            <div className="text-sm"><span className="text-gray-500">Beviljade scopes:</span> {status?.tokenScope ?? '-'}</div>
          </div>

          <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="font-medium">Artiklar i Fortnox</div>
            <p className="text-sm text-gray-600">
              Synkar alla aktiva säljbara produkter från ApisAI till Fortnox som artikelnummer `P&lt;id&gt;`.
            </p>
            <WithWrite moduleCode="settings">
              <button type="button" className="btn btn-success px-2 py-1" onClick={handleSyncArticles} disabled={syncingArticles || !status?.isConnected}>
                {syncingArticles ? 'Synkar artiklar...' : 'Synka artiklar till Fortnox'}
              </button>
            </WithWrite>
          </div>

          {hasCallbackMessage && <div className="text-xs text-gray-500">Uppdaterar Fortnox-status...</div>}
        </section>
      </div>
    </div>
  );
}
